import { motion } from "framer-motion";
import SmartImage from "@/components/site/SmartImage";

const STEPS = [
  {
    number: "01",
    title: "Selectăm doar mașini atent verificate",
    description:
      "Nu cumpărăm orice autovehicul. Alegem doar mașini cu istoric clar și într-o stare tehnică foarte bună.",
    image:
      "/selectam1.jpg",
    alt: "Selecție mașini AutoRIO",
  },
  {
    number: "02",
    title: "Verificăm istoricul complet",
    description:
      "Analizăm istoricul de service, kilometrii și toate informațiile disponibile înainte ca mașina să intre în stoc.",
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1400&q=80",
    alt: "Verificare istoric auto",
  },
  {
    number: "03",
    title: "Inspecție tehnică",
    description:
      "Fiecare automobil trece printr-o verificare tehnică atentă pentru a identifica eventualele probleme.",
    image:
      "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=1400&q=80",
    alt: "Inspecție tehnică",
  },
  {
    number: "04",
    title: "Pregătire estetică",
    description:
      "Mașina este curățată profesional, cosmetizată și pregătită pentru prezentare și livrare.",
    image:
      "/etapa4.jpg",
    alt: "Pregătire estetică interior mașină AutoRIO",
  },
  {
    number: "05",
    title: "Fotografiere profesională",
    description:
      "Realizăm fotografii de calitate și prezentăm cât mai transparent starea fiecărui autoturism.",
    image:
      "/selectam.jpg",
    alt: "Fotografiere auto profesională AutoRIO",
  },
  {
    number: "06",
    title: "Pregătită pentru noul proprietar",
    description:
      "După toate verificările, automobilul este pregătit pentru predare sau livrare oriunde în România.",
    image:
      "/pregatim.jpg",
    alt: "Predare cheie auto",
  },
];

function StepImage({ src, alt, side }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: side === "right" ? 40 : -40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative"
    >
      <div className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-2xl lg:rounded-3xl overflow-hidden bg-black border border-white/[0.05] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)]">
        <SmartImage
          src={src}
          context="full"
          alt={alt}
          className="w-full h-full object-cover"
        />
      </div>
    </motion.div>
  );
}

function StepText({ number, title, description, side }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: side === "right" ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
      className={`relative ${side === "right" ? "lg:pl-4" : "lg:pr-4 lg:text-right lg:items-end"} flex flex-col`}
    >
      <div
        aria-hidden="true"
        className="font-extrabold leading-none tracking-tighter text-transparent select-none"
        style={{
          fontSize: "clamp(5rem, 12vw, 10rem)",
          WebkitTextStroke: "1.5px #D4AF37",
          textStroke: "1.5px #D4AF37",
        }}
      >
        {number}
      </div>
      <h3 className="mt-4 sm:mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight max-w-xl tracking-tight">
        {title}
      </h3>
      <p className="mt-4 text-base sm:text-lg text-white/55 leading-relaxed max-w-md">
        {description}
      </p>
    </motion.div>
  );
}

export default function ProcessTimeline() {
  return (
    <section className="relative bg-[#0a0a0a] overflow-hidden">
      {/* subtle top gradient divider */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/[0.02] to-transparent" />
      <div className="container-wide py-20 sm:py-28 md:py-32 relative">
        {/* Section header */}
        <div className="max-w-3xl">
          <div className="eyebrow">Procesul AutoRIO</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            Cum pregătim fiecare mașină
            <br className="hidden sm:block" /> înainte să ajungă la{" "}
            <span className="font-serif-italic gold-gradient-text font-medium">
              tine
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/55 leading-relaxed max-w-2xl">
            Fiecare autoturism trece printr-un proces atent, de la selecție până la livrare. Fără
            grabă, fără compromisuri.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative mt-16 sm:mt-24">
          {/* Vertical line — mobile left, desktop center */}
          <div
            aria-hidden="true"
            className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/[0.1] to-transparent left-4 sm:left-6 lg:left-1/2 lg:-translate-x-px"
          />

          <ol className="space-y-16 sm:space-y-24 lg:space-y-32">
            {STEPS.map((step, i) => {
              const isEven = i % 2 === 1; // 02, 04, 06 → image on left
              const desktopSide = isEven ? "left" : "right"; // where the image sits
              return (
                <li key={step.number} className="relative">
                  {/* Timeline dot */}
                  <motion.span
                    aria-hidden="true"
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="absolute top-2 left-4 sm:left-6 lg:left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-[#D4AF37] ring-4 ring-[#0a0a0a] z-10"
                  />

                  {/* Mobile: image + text stacked */}
                  <div className="lg:hidden pl-10 sm:pl-14">
                    <StepText
                      number={step.number}
                      title={step.title}
                      description={step.description}
                      side="right"
                    />
                    <div className="mt-6">
                      <StepImage src={step.image} alt={step.alt} side="right" />
                    </div>
                  </div>

                  {/* Desktop: alternating grid */}
                  <div className="hidden lg:grid lg:grid-cols-2 lg:gap-16 xl:gap-20 items-center">
                    {desktopSide === "left" ? (
                      <>
                        <StepImage src={step.image} alt={step.alt} side="left" />
                        <StepText
                          number={step.number}
                          title={step.title}
                          description={step.description}
                          side="right"
                        />
                      </>
                    ) : (
                      <>
                        <StepText
                          number={step.number}
                          title={step.title}
                          description={step.description}
                          side="left"
                        />
                        <StepImage src={step.image} alt={step.alt} side="right" />
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
